import { z } from "zod";

// ============================================================================
// Enums — mirror /supabase/migrations/0002_enums.sql exactly.
// ============================================================================

export const USER_ROLES = ["internal", "partner"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PARTNER_TYPES = [
  "integration_partner",
  "referral_partner",
  "reseller",
  "agency",
  "consultant",
] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

export const PARTNER_TIERS = ["bronze", "silver", "gold", "platinum"] as const;
export type PartnerTier = (typeof PARTNER_TIERS)[number];

export const PARTNER_STATUSES = ["active", "inactive", "churned"] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

export const DEAL_STATUSES = [
  "submitted",
  "under_review",
  "approved",
  "active",
  "closed_won",
  "closed_lost",
] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

export const DEAL_TYPES = ["referral", "co_sell", "reseller"] as const;
export type DealType = (typeof DEAL_TYPES)[number];

// Mirrors the enum in `supabase/migrations/0050_hubspot_partner_deals.sql`.
// These are the partner→deal association labels in HubSpot's CRM (typeIds 52,
// 54, 62 respectively); the unlabeled typeId 44 is HubSpot's default pair-along
// and is intentionally skipped by the backfill.
export const HUBSPOT_PARTNER_DEAL_RELATIONSHIPS = [
  "referred_to_us",
  "referred_to_them",
  "influenced",
] as const;
export type HubspotPartnerDealRelationship =
  (typeof HUBSPOT_PARTNER_DEAL_RELATIONSHIPS)[number];

export const HUBSPOT_PARTNER_DEAL_RELATIONSHIP_LABEL: Record<
  HubspotPartnerDealRelationship,
  string
> = {
  referred_to_us: "Referred by partner",
  referred_to_them: "Introduced by Bookboost",
  influenced: "Co-influenced by Bookboost",
};

// Direction classification. Only `referred_to_us` counts as a partner-driven
// contribution to Bookboost revenue. The other two are outbound flows where
// Bookboost moved a prospect toward the partner, so they MUST NOT be blended
// into aggregate "partner activity" totals — they get their own metrics. See
// the plan at .claude/plans/we-need-to-snuggly-pond.md and the per-partner
// Deals tab for the established pattern.
export const PARTNER_INBOUND_RELATIONSHIPS = [
  "referred_to_us",
] as const satisfies readonly HubspotPartnerDealRelationship[];

export const PARTNER_OUTBOUND_RELATIONSHIPS = [
  "referred_to_them",
  "influenced",
] as const satisfies readonly HubspotPartnerDealRelationship[];

export const COMMENT_AUTHOR_TYPES = ["internal", "partner"] as const;
export type CommentAuthorType = (typeof COMMENT_AUTHOR_TYPES)[number];

export const ASSET_TYPES = [
  "deck",
  "battlecard",
  "one_pager",
  "case_study",
  "video_link",
  "playbook",
  "template",
  "other",
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_VISIBILITIES = [
  "all_partners",
  "tier_silver_up",
  "tier_gold_up",
  "internal_only",
] as const;
export type AssetVisibility = (typeof ASSET_VISIBILITIES)[number];

// Session 6 — embed providers. Drives the iframe URL normalisation in
// lib/content.ts → embedUrlForProvider. Stored as the postgres enum
// `public.embed_provider` (migration 0040). Adding a provider here also
// requires extending the enum + the helper.
export const EMBED_PROVIDERS = [
  "loom",
  "youtube",
  "vimeo",
  "google_slides",
  "google_docs",
  "google_sheets",
  "hubspot_meeting",
  "linkedin",
  "webpage",
] as const;
export type EmbedProvider = (typeof EMBED_PROVIDERS)[number];

export const TASK_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  // Sub-bucket 4 (migration 0069). journey_task_progress only — onboarding
  // tasks share the same Postgres `task_status` enum but never transition
  // into this state.
  "snoozed",
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

// Session 5 Track 5B — generalized goals. The legacy GOAL_METRIC_TYPES was
// removed in migration 0039 (Q6 confirmed no goals in use; clean refactor).
// The new model is (data_source, aggregation_kind, aggregation_property,
// date_property, attribution, filter, frequency) with per-period targets in
// public.partner_goal_targets.

export const GOAL_AGGREGATION_KINDS = ["count", "sum"] as const;
export type GoalAggregationKind = (typeof GOAL_AGGREGATION_KINDS)[number];

export const GOAL_DATA_SOURCES = ["deal", "submission", "account", "journey_progress"] as const;
export type GoalDataSource = (typeof GOAL_DATA_SOURCES)[number];

export const GOAL_ATTRIBUTIONS = ["first_touch", "last_touch", "multi_touch", "all"] as const;
export type GoalAttribution = (typeof GOAL_ATTRIBUTIONS)[number];

export const GOAL_FREQUENCIES = ["monthly", "quarterly", "yearly", "one_off"] as const;
export type GoalFrequency = (typeof GOAL_FREQUENCIES)[number];

export const GOAL_TARGET_STATUSES = [
  "pending",
  "on_track",
  "at_risk",
  "met",
  "missed",
] as const;
export type GoalTargetStatus = (typeof GOAL_TARGET_STATUSES)[number];

export const GOAL_DATA_SOURCE_LABEL: Record<GoalDataSource, string> = {
  deal: "Deals",
  submission: "Submissions",
  account: "Accounts",
  journey_progress: "Journey progress",
};

export const GOAL_ATTRIBUTION_LABEL: Record<GoalAttribution, string> = {
  first_touch: "First touch",
  last_touch: "Last touch",
  multi_touch: "Multi-touch (Phase B)",
  all: "All",
};

export const GOAL_FREQUENCY_LABEL: Record<GoalFrequency, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  one_off: "One-off",
};

export const GOAL_TARGET_STATUS_LABEL: Record<GoalTargetStatus, string> = {
  pending: "Pending",
  on_track: "On track",
  at_risk: "At risk",
  met: "Met",
  missed: "Missed",
};

// ============================================================================
// Account — cached snapshot of a HubSpot Company that is the target customer
// on a deal (distinct from the referring partner). One row per HubSpot
// Company id; refreshed in the background when the deal drawer is opened and
// the snapshot is older than the staleness threshold. See
// supabase/migrations/0026_accounts.sql and lib/accounts.ts.
//
// Read-only from the client — the link/unlink server actions take the
// HubSpot Company id directly, so no Zod schema is needed here.
// ============================================================================

export type Account = {
  id: string;
  hubspot_company_id: string;
  name: string | null;
  website: string | null;
  industry: string | null;
  annual_revenue: number | null;
  employee_count: number | null;
  description: string | null;
  location_city: string | null;
  location_country: string | null;
  last_synced_at: string;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Display labels — single source of truth for UI rendering.
// ============================================================================

export const PARTNER_TYPE_LABEL: Record<PartnerType, string> = {
  integration_partner: "Integration partner",
  referral_partner: "Referral partner",
  reseller: "Reseller",
  agency: "Agency",
  consultant: "Consultant",
};

export const PARTNER_TIER_LABEL: Record<PartnerTier, string> = {
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum",
};

export const PARTNER_STATUS_LABEL: Record<PartnerStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  churned: "Churned",
};

export const DEAL_STATUS_LABEL: Record<DealStatus, string> = {
  submitted: "Submitted",
  under_review: "Under review",
  approved: "Approved",
  active: "Active",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};

export const DEAL_TYPE_LABEL: Record<DealType, string> = {
  referral: "Referral",
  co_sell: "Co-sell",
  reseller: "Reseller",
};

export const ASSET_TYPE_LABEL: Record<AssetType, string> = {
  deck: "Deck",
  battlecard: "Battlecard",
  one_pager: "One-pager",
  case_study: "Case study",
  video_link: "Video",
  playbook: "Playbook",
  template: "Template",
  other: "Other",
};

export const ASSET_VISIBILITY_LABEL: Record<AssetVisibility, string> = {
  all_partners: "All partners",
  tier_silver_up: "Silver tier & up",
  tier_gold_up: "Gold tier & up",
  internal_only: "Internal only",
};

export const EMBED_PROVIDER_LABEL: Record<EmbedProvider, string> = {
  loom: "Loom",
  youtube: "YouTube",
  vimeo: "Vimeo",
  google_slides: "Google Slides",
  google_docs: "Google Docs",
  google_sheets: "Google Sheets",
  hubspot_meeting: "HubSpot meeting",
  linkedin: "LinkedIn",
  webpage: "Web page",
};

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  snoozed: "Snoozed",
};

// Pipeline order — used for the kanban board and analytics.
export const DEAL_PIPELINE: DealStatus[] = [
  "submitted",
  "under_review",
  "approved",
  "active",
  "closed_won",
  "closed_lost",
];

// ============================================================================
// Zod schemas — used on every API route and server action.
// ============================================================================

// Phase 9.3 — `optionalText` now accepts `null` in addition to `string` and
// `undefined`. Previously it was `z.string().optional().transform(...)`,
// which REJECTED null with "expected string, received null". That broke
// any double-parse pipeline where an upstream schema had already
// collapsed an empty/missing field to null (e.g. formUpdateMetaRequestSchema
// → updateForm → formUpdateSchema). Accepting null at the boundary makes
// the transform idempotent and keeps the post-parse shape (`string | null`)
// unchanged.
const optionalText = z
  .union([z.string().trim().max(2000), z.null()])
  .optional()
  .transform((v) => (v == null || v === "" ? null : v));

const requiredText = (max = 200) => z.string().trim().min(1).max(max);
const emailField = z.string().trim().email().max(320);

export const partnerCreateSchema = z.object({
  company_name: requiredText(200),
  partner_type: z.enum(PARTNER_TYPES),
  tier: z.enum(PARTNER_TIERS).default("bronze"),
  status: z.enum(PARTNER_STATUSES).default("active"),
  primary_contact_name: optionalText,
  primary_contact_email: z
    .union([z.literal(""), emailField])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  website: z
    .union([z.literal(""), z.string().trim().max(500).url()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  region: optionalText,
  notes: optionalText,
  assigned_owner: optionalText,
  // Optional link to the HubSpot custom Partner object record this row was
  // created from. Set when partners are created via the "Browse HubSpot" tab
  // so future syncs UPDATE the existing record rather than create a new one.
  hubspot_partner_record_id: z
    .string()
    .trim()
    .max(64)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type PartnerCreateInput = z.infer<typeof partnerCreateSchema>;

// Note: `partnerUpdateSchema` allows re-linking `hubspot_partner_record_id` via
// update. Intentional — an internal user may need to repoint a partner row at a
// different HubSpot record (e.g. after a HubSpot merge or fix). Gated by
// `requireInternal()` at the action layer.
//
// Phase 9.3 — `tier` and `status` carry `.default(...)` on the create schema.
// `.partial()` makes them optional but does NOT strip the default, so a PATCH
// that omits them would silently UPDATE the row back to "bronze" / "active".
// Rebuild via `.omit().extend()` to re-add them as optional WITHOUT defaults.
// (No live SPA consumer today — `useUpdatePartner` is exported but unused —
// but the API route `PATCH /api/partners/:id` is publicly exposed, so this
// is a defensive fix that matches the Phase 9.2 formUpdateSchema pattern.)
export const partnerUpdateSchema = partnerCreateSchema
  .partial()
  .omit({ tier: true, status: true })
  .extend({
    tier: z.enum(PARTNER_TIERS).optional(),
    status: z.enum(PARTNER_STATUSES).optional(),
    // 0091: link to the integrations-marketplace listing. Update-only (the
    // link is set post-creation via the admin partner detail UI); null clears.
    marketplace_integration_id: z.number().int().positive().nullable().optional(),
  });
export type PartnerUpdateInput = z.infer<typeof partnerUpdateSchema>;

export const dealCreateSchema = z.object({
  partner_id: z.string().uuid().optional(), // server fills for partner-portal submissions
  title: requiredText(200),
  status: z.enum(DEAL_STATUSES).default("submitted"),
  deal_type: z.enum(DEAL_TYPES),
  prospect_company: requiredText(200),
  prospect_contact_name: optionalText,
  prospect_contact_email: z
    .union([z.literal(""), emailField])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  estimated_value_eur: z
    .union([z.literal(""), z.coerce.number().int().nonnegative().max(100_000_000)])
    .optional()
    .transform((v) => (v == null || v === "" ? null : Number(v))),
  notes: optionalText,
  assigned_to: optionalText,
  // Track 8E — optional MDF attribution. Empty string from a <select> form
  // collapses to null so admins and partners can clear the link the same
  // way they'd clear any other optional select.
  mdf_request_id: z
    .union([z.literal(""), z.string().uuid()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type DealCreateInput = z.infer<typeof dealCreateSchema>;

// Phase 9.3 — `status` has `.default("submitted")` on the create schema.
// `.partial()` does NOT strip defaults, so a PATCH that omits `status` would
// silently UPDATE the row back to "submitted". Rebuild via `.omit().extend()`.
// (No live consumer today — exported but unused — defensive fix.)
export const dealUpdateSchema = dealCreateSchema
  .partial()
  .omit({ status: true })
  .extend({
    status: z.enum(DEAL_STATUSES).optional(),
  });
export type DealUpdateInput = z.infer<typeof dealUpdateSchema>;

export const dealStatusUpdateSchema = z.object({
  status: z.enum(DEAL_STATUSES),
});
export type DealStatusUpdateInput = z.infer<typeof dealStatusUpdateSchema>;

export const dealCommentCreateSchema = z.object({
  content: z.string().trim().min(1).max(8000),
});
export type DealCommentCreateInput = z.infer<typeof dealCommentCreateSchema>;

// Session 6 — content asset schema widened for folders + embeds + categories
// + parent FK + archive. The four shapes mirror the CHECK constraints in
// migration 0040: folder (no source fields, no parent), file upload (file_url
// only), external link (external_url only), or embed (embed_provider +
// embed_url). A row must satisfy exactly one shape.
export const contentAssetCreateSchema = z
  .object({
    title: requiredText(200),
    description: optionalText,
    asset_type: z.enum(ASSET_TYPES),
    is_folder: z.coerce.boolean().default(false),
    // These optional fields accept null as well as "" / undefined: the SPA's
    // AddAssetMenu sends explicit `null` for every shape-irrelevant field
    // (e.g. a file upload sends external_url/embed_* = null). `.optional()`
    // alone rejects null with "Invalid input"; the `z.null()` branch mirrors
    // `optionalText` above and the `v == null` transform that follows.
    parent_asset_id: z
      .union([z.literal(""), z.string().uuid(), z.null()])
      .optional()
      .transform((v) => (v == null || v === "" ? null : v)),
    // Storage path, not a URL: the SPA sends `content/<storageKey>` after the
    // direct-to-Storage upload and the read path strips the `content/` prefix
    // before signing (services/content.ts). Mirrors contentAssetUpdateSchema's
    // file_url below — the create schema was missed when the update schema was
    // fixed for the replace-file flow.
    file_url: z
      .union([z.literal(""), z.string().trim().min(1).max(500), z.null()])
      .optional()
      .transform((v) => (v == null || v === "" ? null : v)),
    external_url: z
      .union([z.literal(""), z.string().trim().url(), z.null()])
      .optional()
      .transform((v) => (v == null || v === "" ? null : v)),
    embed_provider: z
      .union([z.literal(""), z.enum(EMBED_PROVIDERS), z.null()])
      .optional()
      .transform((v) => (v == null || v === "" ? null : (v as EmbedProvider))),
    embed_url: z
      .union([z.literal(""), z.string().trim().url(), z.null()])
      .optional()
      .transform((v) => (v == null || v === "" ? null : v)),
    visibility: z.enum(ASSET_VISIBILITIES).default("all_partners"),
    partner_type_restriction: z.array(z.enum(PARTNER_TYPES)).default([]),
    categories: z.array(z.string().trim().min(1).max(60)).default([]),
    uploaded_by: optionalText,
  })
  .superRefine((v, ctx) => {
    if (v.is_folder) {
      if (v.file_url || v.external_url || v.embed_provider || v.embed_url || v.parent_asset_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["is_folder"],
          message:
            "Folders cannot have a file, link, embed, or parent — clear those fields first.",
        });
      }
      return;
    }
    const hasFile = !!v.file_url;
    const hasLink = !!v.external_url;
    const hasEmbed = !!v.embed_provider;
    if (hasEmbed && !v.embed_url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["embed_url"],
        message: "Embed URL is required when a provider is selected.",
      });
    }
    if (hasEmbed && (hasFile || hasLink)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["embed_provider"],
        message: "An embed asset cannot also have a file or external link.",
      });
    }
    if (!hasFile && !hasLink && !hasEmbed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file_url"],
        message: "Provide a file, external link, or embed.",
      });
    }
  });
export type ContentAssetCreateInput = z.infer<typeof contentAssetCreateSchema>;

// Partial schema for inline edits. Same superRefine doesn't apply on partial
// (callers must re-check after merging onto the existing row when changing
// shapes). Lighter validation is intentional — write actions in lib/content.ts
// merge the patch into the current row and re-validate via the full schema.
export const contentAssetUpdateSchema = z.object({
  title: requiredText(200).optional(),
  description: optionalText,
  asset_type: z.enum(ASSET_TYPES).optional(),
  // Same null-tolerance as contentAssetCreateSchema: edit-asset-dialog.tsx
  // sends `embed_url`/`external_url` as `null` when clearing a URL, and
  // `.optional()` alone would reject null with "Invalid input". The `z.null()`
  // branch + `v == null` transform mirror the create schema and optionalText.
  embed_provider: z
    .union([z.literal(""), z.enum(EMBED_PROVIDERS), z.null()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : (v as EmbedProvider))),
  embed_url: z
    .union([z.literal(""), z.string().trim().url(), z.null()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  external_url: z
    .union([z.literal(""), z.string().trim().url(), z.null()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  // Phase 7 sub-bucket 19 — replace-file action. The asset-preview's new
  // "Replace file" button calls the existing upload-init → direct PUT → PATCH
  // chain, and the PATCH carries the new storage key here. Accepts both bare
  // storage keys (legacy parity) and the `content/<key>` prefixed form the
  // upload-init handler returns; the service normalises before persisting.
  file_url: z
    .union([z.literal(""), z.string().trim().min(1).max(500), z.null()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  visibility: z.enum(ASSET_VISIBILITIES).optional(),
  partner_type_restriction: z.array(z.enum(PARTNER_TYPES)).optional(),
  categories: z.array(z.string().trim().min(1).max(60)).optional(),
});
export type ContentAssetUpdateInput = z.infer<typeof contentAssetUpdateSchema>;

export const contentAssetMoveSchema = z.object({
  id: z.string().uuid(),
  parent_asset_id: z
    .union([z.literal(""), z.string().uuid()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type ContentAssetMoveInput = z.infer<typeof contentAssetMoveSchema>;

export const onboardingTaskCreateSchema = z.object({
  partner_id: z.string().uuid(),
  title: requiredText(200),
  description: optionalText,
  due_date: z
    .union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  assigned_to_partner: z.coerce.boolean().default(false),
  status: z.enum(TASK_STATUSES).default("pending"),
});
export type OnboardingTaskCreateInput = z.infer<typeof onboardingTaskCreateSchema>;

// Session 5 Track 5B — partner_goals + partner_goal_targets schemas.
// `aggregation_property` must be present when aggregation_kind=sum and absent
// when aggregation_kind=count (mirrored by the CHECK in migration 0039).

const isoDateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const partnerGoalCreateSchema = z
  .object({
    partner_id: z.string().uuid().nullable().optional(),
    title: requiredText(200),
    description: optionalText,
    aggregation_kind: z.enum(GOAL_AGGREGATION_KINDS),
    aggregation_property: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .optional()
      .nullable(),
    data_source: z.enum(GOAL_DATA_SOURCES),
    date_property: z.string().trim().min(1).max(120),
    attribution: z.enum(GOAL_ATTRIBUTIONS).default("all"),
    // FilterTree shape — same JSONB schema as views.filter / segments.filter.
    // Tolerate an empty group so a goal can save without a filter applied.
    // Lazy reference because filterGroupSchema is declared later in this file.
    filter: z.lazy(() => filterGroupSchema),
    frequency: z.enum(GOAL_FREQUENCIES),
  })
  .superRefine((val, ctx) => {
    if (val.aggregation_kind === "sum" && !val.aggregation_property) {
      ctx.addIssue({
        code: "custom",
        path: ["aggregation_property"],
        message: "Property is required when aggregation is Sum.",
      });
    }
    if (val.aggregation_kind === "count" && val.aggregation_property) {
      ctx.addIssue({
        code: "custom",
        path: ["aggregation_property"],
        message: "Leave Property empty when aggregation is Count.",
      });
    }
  });
export type PartnerGoalCreateInput = z.infer<typeof partnerGoalCreateSchema>;

export const partnerGoalUpdateSchema = z
  .object({
    // Phase 7 sub-bucket 19 — partner-scope edits accepted. A `null`
    // partner_id flips the goal to "all partners" scope; a UUID swaps the
    // owner. The goal-definition pane gates this with a confirmation prompt
    // because the existing `partner_goal_targets` rows are scoped to the
    // OLD partner_id and won't auto-migrate. Legacy parity: the toast hint
    // in goal-definition-pane is now removed.
    partner_id: z.string().uuid().nullable().optional(),
    title: requiredText(200).optional(),
    description: z
      .string()
      .trim()
      .max(2000)
      .optional()
      .nullable()
      .transform((v) => (v == null || v === "" ? null : v))
      .optional(),
    aggregation_kind: z.enum(GOAL_AGGREGATION_KINDS).optional(),
    aggregation_property: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .optional()
      .nullable(),
    data_source: z.enum(GOAL_DATA_SOURCES).optional(),
    date_property: z.string().trim().min(1).max(120).optional(),
    attribution: z.enum(GOAL_ATTRIBUTIONS).optional(),
    filter: z.lazy(() => filterGroupSchema).optional(),
    frequency: z.enum(GOAL_FREQUENCIES).optional(),
    is_archived: z.boolean().optional(),
  });
export type PartnerGoalUpdateInput = z.infer<typeof partnerGoalUpdateSchema>;

export const partnerGoalTargetCreateSchema = z
  .object({
    goal_id: z.string().uuid(),
    partner_id: z.string().uuid(),
    period_start: isoDateStr,
    period_end: isoDateStr,
    target_value: z.coerce.number().positive(),
  })
  .superRefine((v, ctx) => {
    if (v.period_end < v.period_start) {
      ctx.addIssue({
        code: "custom",
        path: ["period_end"],
        message: "Period end must be on or after period start.",
      });
    }
  });
export type PartnerGoalTargetCreateInput = z.infer<typeof partnerGoalTargetCreateSchema>;

export const partnerGoalTargetUpdateSchema = z.object({
  target_value: z.coerce.number().positive().optional(),
  current_value_override: z.coerce.number().nullable().optional(),
  status: z.enum(GOAL_TARGET_STATUSES).optional(),
});
export type PartnerGoalTargetUpdateInput = z.infer<typeof partnerGoalTargetUpdateSchema>;

// Strongly-typed row shapes for the app layer. JSONB columns are narrowed via
// filterTreeSchema; the loose `Json` versions live in types/database.types.ts.
export type PartnerGoal = {
  id: string;
  partner_id: string | null;
  title: string;
  description: string | null;
  aggregation_kind: GoalAggregationKind;
  aggregation_property: string | null;
  data_source: GoalDataSource;
  date_property: string;
  attribution: GoalAttribution;
  filter: FilterGroup;
  frequency: GoalFrequency;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type PartnerGoalTarget = {
  id: string;
  goal_id: string;
  partner_id: string;
  period_start: string;
  period_end: string;
  target_value: number;
  current_value: number;
  current_value_override: number | null;
  current_value_computed_at: string | null;
  status: GoalTargetStatus;
  created_at: string;
  updated_at: string;
};

export const profileCompletionSchema = z.object({
  full_name: requiredText(160),
  role_at_partner: requiredText(120),
  website: z
    .union([z.literal(""), z.string().trim().url()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  // I1: PMS / systems the partner integrates with. Free tags, deduped +
  // trimmed; omitted leaves the stored value untouched.
  integrates_with: z
    .array(z.string().trim().min(1).max(80))
    .max(30)
    .optional(),
});
export type ProfileCompletionInput = z.infer<typeof profileCompletionSchema>;

export const inviteCreateSchema = z.object({
  email: emailField,
  partner_id: z.string().uuid(),
  partner_user_id: z.string().uuid().optional(),
  role: z.enum(USER_ROLES).default("partner"),
});
export type InviteCreateInput = z.infer<typeof inviteCreateSchema>;

// ============================================================================
// Roles + role permissions — see supabase/migrations/0021_roles.sql
// ============================================================================

// Flat string-based action namespace. Kept in sync with the seed in 0021.
// New permission strings are added here when the corresponding feature lands;
// the UI's permission grouping logic keys off the prefix before the dot.
export const PERMISSION_ACTIONS = [
  "partners.read",
  "partners.write",
  "partners.delete",
  "deals.read",
  "deals.write",
  "deals.delete",
  "content.read",
  "content.write",
  "content.delete",
  "commissions.read",
  "commissions.write",
  "analytics.read",
  "settings.read",
  "settings.write",
  "announcements.write",
  "invites.write",
  "roles.read",
  "roles.manage",
  // Session 3 — Forms + Submissions. Seeded in 0030_widen_entity_constraints.
  "forms.read",
  "forms.write",
  "submissions.read",
  "submissions.write",
  "submissions.review",
  // Session 1 retrofit (migration 0032) — split Accounts RLS into
  // read / write so CRM User can read snapshots without mutating them.
  "accounts.read",
  "accounts.write",
  // Session 5 Track 5A — Tier programs.
  "tiers.read",
  "tiers.manage",
  // Session 5 Track 5B — Generalised Goals.
  "goals.read",
  "goals.manage",
  // Session 7 — Engagement engine. Seeded in 0042_notifications.sql.
  "notifications.read",
  "notifications.write",
  // Session 8 — Marketing Development Funds. Seeded in 0048_mdf_seeds.sql.
  "mdf.read",
  "mdf.write",
  "mdf.approve",
  // User Manager — unified internal + partner user admin. Seeded in
  // 0083_user_manager.sql.
  "users.read",
  "users.manage",
  // Marketplace Listing Self-Service. Seeded in 0091_marketplace_listings.sql.
  "listings.read",
  "listings.review",
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export const PERMISSION_ACTION_LABEL: Record<PermissionAction, string> = {
  "partners.read": "View partners",
  "partners.write": "Create + edit partners",
  "partners.delete": "Delete partners",
  "deals.read": "View deals",
  "deals.write": "Create + edit deals",
  "deals.delete": "Delete deals",
  "content.read": "View content library",
  "content.write": "Upload + edit content",
  "content.delete": "Delete content",
  "commissions.read": "View commissions",
  "commissions.write": "Manage commissions",
  "analytics.read": "View analytics",
  "settings.read": "View settings",
  "settings.write": "Edit settings",
  "announcements.write": "Manage announcements",
  "invites.write": "Invite team members",
  "roles.read": "View roles",
  "roles.manage": "Manage roles + permissions",
  "forms.read": "View forms",
  "forms.write": "Create + edit forms",
  "submissions.read": "View submissions",
  "submissions.write": "Submit + edit submissions",
  "submissions.review": "Accept / decline submissions",
  "accounts.read": "View account snapshots",
  "accounts.write": "Edit account snapshots",
  "tiers.read": "View tier programs",
  "tiers.manage": "Manage tier programs + assignments",
  "goals.read": "View goals",
  "goals.manage": "Create + edit goals",
  "notifications.read": "View notification rules + delivery log",
  "notifications.write": "Manage notification rules + audiences",
  "mdf.read": "View marketing funds + requests",
  "mdf.write": "Create + edit marketing funds + allocations",
  "mdf.approve": "Approve / decline marketing-fund requests",
  "users.read": "View users (team + partner contacts)",
  "users.manage": "Invite + manage users and account access",
  "listings.read": "View marketplace listing changes",
  "listings.review": "Approve / decline marketplace listing changes",
};

// Display groupings for the role-edit UI. The key is the namespace prefix
// (before the dot); the value is the section heading shown in the form.
export const PERMISSION_GROUPS: Array<{ key: string; label: string }> = [
  { key: "partners", label: "Partners" },
  { key: "deals", label: "Deals" },
  { key: "accounts", label: "Accounts" },
  { key: "content", label: "Content" },
  { key: "commissions", label: "Commissions" },
  { key: "analytics", label: "Analytics" },
  { key: "announcements", label: "Announcements" },
  { key: "invites", label: "Invites" },
  { key: "settings", label: "Settings" },
  { key: "roles", label: "Roles" },
  { key: "forms", label: "Forms" },
  { key: "submissions", label: "Submissions" },
  { key: "tiers", label: "Tier programs" },
  { key: "goals", label: "Goals" },
  { key: "notifications", label: "Notifications" },
  { key: "users", label: "Users" },
  { key: "listings", label: "Marketplace listings" },
];

const roleKeyPattern = /^[a-z][a-z0-9_]*$/;

export const roleCreateSchema = z.object({
  key: z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(roleKeyPattern, {
      message: "Use lowercase letters, numbers and underscores only",
    }),
  name: requiredText(60),
  description: optionalText,
  permissions: z.array(z.enum(PERMISSION_ACTIONS)).default([]),
});
export type RoleCreateInput = z.infer<typeof roleCreateSchema>;

export const roleUpdateSchema = z.object({
  name: requiredText(60),
  description: optionalText,
  permissions: z.array(z.enum(PERMISSION_ACTIONS)).default([]),
});
export type RoleUpdateInput = z.infer<typeof roleUpdateSchema>;

// ============================================================================
// Segments — saved boolean filter expressions reused by Saved Views (Track 1A)
// and audience scoping (Session 7). See supabase/migrations/0020_segments.sql
// and lib/segments.ts for the full schema, field metadata and helpers.
// ============================================================================

// Entity types currently understood by the FilterTreeBuilder UI. Mirror the
// CHECK constraint in 0020 (widened in 0030 to add 'submission'). Adding a
// new entity here means a new migration to widen the constraint AND new
// metadata in SEGMENT_FIELDS.
export const SEGMENT_ENTITY_TYPES = [
  "partner",
  "deal",
  "submission",
  "journey",
  "content_asset",
  "mdf_request",
] as const;
export type SegmentEntityType = (typeof SEGMENT_ENTITY_TYPES)[number];

export const SEGMENT_ENTITY_LABEL: Record<SegmentEntityType, string> = {
  partner: "Partners",
  deal: "Deals",
  submission: "Submissions",
  journey: "Journeys",
  content_asset: "Content",
  mdf_request: "MDF requests",
};

// Phase 7 sub-bucket 18 — superset of SEGMENT_ENTITY_TYPES that includes
// admin-side surfaces not exposed via segments. `admin_deal` is the
// HubSpot-pipeline kanban (vs `deal`, the partner-submitted deal-
// registration table). Saved Views land on admin_deal; segments do not.
export const VIEW_ENTITY_TYPES = [
  ...SEGMENT_ENTITY_TYPES,
  "admin_deal",
] as const;
export type ViewEntityType = (typeof VIEW_ENTITY_TYPES)[number];

export const VIEW_ENTITY_LABEL: Record<ViewEntityType, string> = {
  ...SEGMENT_ENTITY_LABEL,
  admin_deal: "Admin deals",
};

// Leaf operators. Renderable subsets per field type live in lib/segments.ts.
export const FILTER_OPERATORS = [
  "equals",
  "not_equals",
  "in",
  "not_in",
  "contains",
  "not_contains",
  "starts_with",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "is_null",
  "is_not_null",
  "in_last_days",
  "in_next_days",
] as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[number];

// JSONB schema version. Every node embeds this so we can fan out additive
// changes (new operators, new node kinds) without losing existing rows.
const FILTER_SCHEMA_VERSION = 1 as const;

// Tolerated leaf values. The Zod union below intentionally lists every
// reasonable shape — we *don't* try to narrow value-by-operator here; the UI
// is the source of truth for what gets stored, and SQL translation (in the
// Track 1A consumer) re-validates per-operator.
const filterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  z.tuple([z.string(), z.string()]),
  z.tuple([z.number(), z.number()]),
  z.null(),
]);

// Recursive group + condition union. Zod v4 expects the recursive shape to be
// declared via `z.lazy` for the group's `children` array.
const filterConditionSchema = z
  .object({
    kind: z.literal("condition"),
    version: z.literal(FILTER_SCHEMA_VERSION),
    field: z.string().min(1).max(120),
    operator: z.enum(FILTER_OPERATORS),
    value: filterValueSchema.optional(),
  })
  .strict();

export const filterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z
    .object({
      kind: z.literal("group"),
      version: z.literal(FILTER_SCHEMA_VERSION),
      combinator: z.enum(["AND", "OR"]),
      children: z.array(z.union([filterConditionSchema, filterGroupSchema])),
    })
    .strict()
);

export type FilterCondition = z.infer<typeof filterConditionSchema>;
export type FilterGroup = {
  kind: "group";
  version: typeof FILTER_SCHEMA_VERSION;
  combinator: "AND" | "OR";
  children: Array<FilterCondition | FilterGroup>;
};
export type FilterNode = FilterCondition | FilterGroup;
// The top-level shape stored in `segments.filter` is always a group.
export type FilterTree = FilterGroup;

export { filterConditionSchema };

// Top-level filter must be a group containing at least one leaf condition
// somewhere in the tree. Empty trees are rejected with a friendly message.
export const filterTreeSchema = filterGroupSchema.refine(hasAtLeastOneCondition, {
  message: "Add at least one condition before saving",
});

function hasAtLeastOneCondition(node: FilterNode): boolean {
  if (node.kind === "condition") return true;
  return node.children.some(hasAtLeastOneCondition);
}

export const segmentCreateSchema = z.object({
  entity_type: z.enum(SEGMENT_ENTITY_TYPES),
  name: requiredText(120),
  description: optionalText,
  filter: filterTreeSchema,
});
export type SegmentCreateInput = z.infer<typeof segmentCreateSchema>;

// entity_type is intentionally *not* updatable — changing it invalidates the
// stored filter (fields belong to one entity type).
export const segmentUpdateSchema = z.object({
  name: requiredText(120),
  description: optionalText,
  filter: filterTreeSchema,
});
export type SegmentUpdateInput = z.infer<typeof segmentUpdateSchema>;

// ============================================================================
// Saved Views — Track 1A. See supabase/migrations/0019_views.sql.
//
// A view is a named, reorderable tab on a list page. It captures:
//   • An entity_type (currently 'partner' is wired up; 'deal' is reserved).
//   • An inline FilterTree (or null for "no filter").
//   • A list of ViewColumnSpec — column key + visibility + optional width,
//     stored in render order.
//   • An optional ViewSort.
//   • A `scope` of 'private' (creator-only) or 'shared' (all internal).
// Default views (is_system=true) ship from migration 0019 and cannot be
// edited or deleted through the UI.
// ============================================================================

export const VIEW_SCOPES = ["private", "shared"] as const;
export type ViewScope = (typeof VIEW_SCOPES)[number];

export const VIEW_SCOPE_LABEL: Record<ViewScope, string> = {
  private: "Private",
  shared: "Shared",
};

// One row of the JSONB `columns` array on `public.views`. Order is render
// order — the UI reads positions from the array index. `width` is optional;
// when unset the table falls back to its natural column sizing.
//
// Type + schema are kept in lockstep by deriving the type from the Zod
// schema via `z.infer` — avoids the previous duplication where edits had to
// touch two declarations.
const viewColumnSpecSchema = z
  .object({
    key: z.string().trim().min(1).max(120),
    visible: z.boolean(),
    width: z.number().int().positive().max(2000).optional(),
  })
  .strict();

const viewSortSchema = z
  .object({
    column: z.string().trim().min(1).max(120),
    direction: z.enum(["asc", "desc"]),
  })
  .strict();

export type ViewColumnSpec = z.infer<typeof viewColumnSpecSchema>;
export type ViewSort = z.infer<typeof viewSortSchema>;

// Create schema. The filter is optional + nullable — "no filter" is a
// perfectly legitimate view (e.g. the seeded "All partners"). `columns`
// defaults to an empty array; the consumer typically passes the entity's
// default column list explicitly. Uses VIEW_ENTITY_TYPES (superset of
// SEGMENT_ENTITY_TYPES) so admin-only entities like `admin_deal` can carry
// saved views even though segments aren't supported for them.
export const viewCreateSchema = z.object({
  entity_type: z.enum(VIEW_ENTITY_TYPES),
  name: requiredText(200),
  filter: filterTreeSchema.optional().nullable(),
  columns: z.array(viewColumnSpecSchema).default([]),
  sort: viewSortSchema.optional().nullable(),
  scope: z.enum(VIEW_SCOPES).default("private"),
});
export type ViewCreateInput = z.infer<typeof viewCreateSchema>;

// Update schema mirrors create but every field is optional — server actions
// merge into the existing row. `entity_type` is intentionally omitted from
// the *update* path because changing it invalidates filter + columns; if you
// ever need to "move" a view across entities, delete + recreate.
export const viewUpdateSchema = z.object({
  name: requiredText(200).optional(),
  filter: filterTreeSchema.optional().nullable(),
  columns: z.array(viewColumnSpecSchema).optional(),
  sort: viewSortSchema.optional().nullable(),
  scope: z.enum(VIEW_SCOPES).optional(),
});
export type ViewUpdateInput = z.infer<typeof viewUpdateSchema>;

export { viewColumnSpecSchema, viewSortSchema };

export const announcementCreateSchema = z.object({
  body: z.string().trim().min(1).max(500),
  active: z.coerce.boolean().default(true),
  starts_at: z
    .union([z.literal(""), z.string().datetime()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  ends_at: z
    .union([z.literal(""), z.string().datetime()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  // Session 7 Track 7C — scope. NULL on any one = no filter on that
  // dimension. The RLS policy ANDs all three together, so an unrestricted
  // announcement keeps the same broadcast semantics as today.
  audience_segment_id: z
    .union([z.literal(""), z.string().uuid()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  tier_scope: z
    .array(z.enum(PARTNER_TIERS))
    .optional()
    .transform((v) => (v == null || v.length === 0 ? null : v)),
  partner_type_scope: z
    .array(z.enum(PARTNER_TYPES))
    .optional()
    .transform((v) => (v == null || v.length === 0 ? null : v)),
});
export type AnnouncementCreateInput = z.infer<typeof announcementCreateSchema>;

// ============================================================================
// Forms + Submissions — Session 3.
//
// The `definition` JSONB on public.forms is a versioned, discriminated-union
// tree: { version, title, description?, submitButtonLabel, sections: [
//   { id, heading?, description?, fields: FormField[] }
// ] }. Every field carries a stable `id`, a machine-readable `name` (used as
// the JSON key in payload + the downstream column name), a `label`, and
// kind-specific config. The renderer (Track 3C) reads this tree top-to-
// bottom and builds an RHF + Zod form.
//
// Two related JSONB columns sit next to `definition`:
//   • `workflow`   — what to do on accept: downstream_kind + auto_accept +
//                    a payload-key → downstream-column mapping table.
//   • `automation` — fire-and-forget side effects on submit. Schema only in
//                    Session 3; executor lands in Session 7.
//
// See supabase/migrations/0028_forms.sql and 0029_form_submissions.sql.
// ============================================================================

export const FORM_FIELD_KINDS = [
  "text",
  "long_text",
  "email",
  "number",
  "date",
  "partner_lookup",
  "section_heading",
  "file",
  "select",
  "checkbox",
] as const;
export type FormFieldKind = (typeof FORM_FIELD_KINDS)[number];

export const FORM_STATUSES = ["draft", "published", "archived"] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

export const FORM_STATUS_LABEL: Record<FormStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

export const SUBMISSION_STATUSES = ["pending", "accepted", "declined"] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const SUBMISSION_STATUS_LABEL: Record<SubmissionStatus, string> = {
  pending: "Pending",
  accepted: "Accepted",
  declined: "Declined",
};

export const SUBMISSION_DOWNSTREAM_KINDS = [
  "deal",
  "lead",
  "partner_application",
  "contact",
  "company",
] as const;
export type SubmissionDownstreamKind = (typeof SUBMISSION_DOWNSTREAM_KINDS)[number];

// Reserved field machine-names. These collide with form_submissions columns
// (or with the meta-fields the renderer injects on top of payload) and would
// produce ambiguous downstream mappings, so the builder rejects them.
export const RESERVED_FIELD_NAMES: ReadonlySet<string> = new Set([
  "id",
  "status",
  "status_history",
  "created_at",
  "updated_at",
  "partner_id",
  "submitted_by_user_id",
  "form_id",
  "payload",
]);

// Field-name pattern: lowercase letter to start, then lowercase/digits/_.
// Matches typical SQL column-name conventions; the same regex is enforced by
// `formFieldSchema` below so downstream mapping never has to escape names.
const FIELD_NAME_RE = /^[a-z][a-z0-9_]{0,49}$/;

const crmMappingSchema = z
  .object({
    object: z.enum(["deal", "contact", "company", "partner", "lead"]),
    property: z.string().trim().min(1).max(120),
  })
  .strict();

// Common field properties — kind-specific schemas merge on top via .extend().
const baseFieldShape = {
  id: z.string().uuid(),
  name: z
    .string()
    .regex(FIELD_NAME_RE, {
      message:
        "Use lowercase letters, digits and underscores; must start with a letter",
    })
    .refine((n) => !RESERVED_FIELD_NAMES.has(n), {
      message: "This name is reserved",
    }),
  label: z.string().trim().min(1).max(200),
  placeholder: z.string().trim().max(200).optional(),
  helpText: z.string().trim().max(500).optional(),
  required: z.boolean().default(false),
  crmMapping: crmMappingSchema.optional(),
};

const textFieldSchema = z
  .object({
    kind: z.literal("text"),
    ...baseFieldShape,
    maxLength: z.number().int().positive().max(10_000).optional(),
  })
  .strict();

const longTextFieldSchema = z
  .object({
    kind: z.literal("long_text"),
    ...baseFieldShape,
    maxLength: z.number().int().positive().max(100_000).optional(),
  })
  .strict();

const emailFieldSchema = z
  .object({
    kind: z.literal("email"),
    ...baseFieldShape,
  })
  .strict();

const numberFieldSchema = z
  .object({
    kind: z.literal("number"),
    ...baseFieldShape,
    integer: z.boolean().default(true),
    min: z.number().optional(),
    max: z.number().optional(),
  })
  .strict();

const dateFieldSchema = z
  .object({
    kind: z.literal("date"),
    ...baseFieldShape,
  })
  .strict();

const partnerLookupFieldSchema = z
  .object({
    kind: z.literal("partner_lookup"),
    ...baseFieldShape,
  })
  .strict();

// section_heading is a display-only field — no input, no payload key. We
// still carry id + name + label so the builder UI can show it in the canvas
// like any other field; `name` is not required to be unique (it's never
// emitted into the payload — see the superRefine in formDefinitionSchema).
const sectionHeadingFieldSchema = z
  .object({
    kind: z.literal("section_heading"),
    ...baseFieldShape,
  })
  .strict();

const fileFieldSchema = z
  .object({
    kind: z.literal("file"),
    ...baseFieldShape,
    maxSizeBytes: z.number().int().positive().max(50 * 1024 * 1024).optional(),
    acceptedMimeTypes: z.array(z.string().trim().min(1).max(120)).optional(),
  })
  .strict();

const selectOptionSchema = z
  .object({
    value: z.string().trim().min(1).max(200),
    label: z.string().trim().min(1).max(200),
  })
  .strict();

const selectFieldSchema = z
  .object({
    kind: z.literal("select"),
    ...baseFieldShape,
    multiple: z.boolean().default(false),
    options: z.array(selectOptionSchema).min(1).max(200),
  })
  .strict();

const checkboxFieldSchema = z
  .object({
    kind: z.literal("checkbox"),
    ...baseFieldShape,
  })
  .strict();

export const formFieldSchema = z.discriminatedUnion("kind", [
  textFieldSchema,
  longTextFieldSchema,
  emailFieldSchema,
  numberFieldSchema,
  dateFieldSchema,
  partnerLookupFieldSchema,
  sectionHeadingFieldSchema,
  fileFieldSchema,
  selectFieldSchema,
  checkboxFieldSchema,
]);
export type FormField = z.infer<typeof formFieldSchema>;
export type FormFieldByKind<K extends FormFieldKind> = Extract<FormField, { kind: K }>;

export const formSectionSchema = z
  .object({
    id: z.string().uuid(),
    heading: z.string().trim().max(200).optional().nullable(),
    description: z.string().trim().max(2000).optional().nullable(),
    fields: z.array(formFieldSchema),
  })
  .strict();
export type FormSection = z.infer<typeof formSectionSchema>;

export const FORM_DEFINITION_VERSION = 1 as const;

export const formDefinitionSchema = z
  .object({
    version: z.literal(FORM_DEFINITION_VERSION),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    submitButtonLabel: z.string().trim().min(1).max(80).default("Submit"),
    sections: z.array(formSectionSchema).min(1),
  })
  .strict()
  .superRefine((def, ctx) => {
    // Enforce unique field names across all sections (section_heading
    // intentionally excluded — it's display-only and never emitted as a
    // payload key, so reusing a "name" of e.g. "intro" across multiple
    // section_heading rows is fine).
    const seen = new Map<string, { section: number; field: number }>();
    def.sections.forEach((section, sectionIdx) => {
      section.fields.forEach((field, fieldIdx) => {
        if (field.kind === "section_heading") return;
        const prior = seen.get(field.name);
        if (prior) {
          ctx.addIssue({
            code: "custom",
            path: ["sections", sectionIdx, "fields", fieldIdx, "name"],
            message: `Field name '${field.name}' is already used in section ${prior.section + 1}`,
          });
          return;
        }
        seen.set(field.name, { section: sectionIdx, field: fieldIdx });
      });
    });
  });
export type FormDefinition = z.infer<typeof formDefinitionSchema>;

// ---------------------------------------------------------------------------
// Workflow + automation config — Phase A.
//
// Workflow controls what happens when a submission is accepted:
//   • downstream_kind: which downstream object (deal, lead, etc.) is
//     created. 'none' = no downstream object.
//   • auto_accept:    if true, submissions are auto-accepted on submit
//                     (used by the deal-registration form to preserve the
//                     existing UX).
//   • mapping:        a map of `payload key → downstream column name` used
//                     by lib/forms.ts → acceptSubmission to build the
//                     downstream insert. Identity-by-default semantics if
//                     a key is missing.
// ---------------------------------------------------------------------------
export const formWorkflowAcceptanceSchema = z
  .object({
    downstream_kind: z.enum([
      "deal",
      "lead",
      "partner_application",
      "contact",
      "company",
      "none",
    ]),
    auto_accept: z.boolean().default(false),
    mapping: z.record(z.string(), z.string()).default({}),
  })
  .strict();
export type FormWorkflowAcceptance = z.infer<typeof formWorkflowAcceptanceSchema>;

export const formWorkflowConfigSchema = z
  .object({
    acceptance: formWorkflowAcceptanceSchema.default({
      downstream_kind: "none",
      auto_accept: false,
      mapping: {},
    }),
  })
  .strict();
export type FormWorkflowConfig = z.infer<typeof formWorkflowConfigSchema>;

// ---------------------------------------------------------------------------
// Automation config — schema only in Session 3. The dispatcher in Session 7
// reads these entries and runs them post-submit.
// ---------------------------------------------------------------------------
const automationSlackSchema = z
  .object({
    kind: z.literal("slack"),
    channel: z.string().trim().min(1).max(200),
  })
  .strict();

const automationEmailSchema = z
  .object({
    kind: z.literal("email"),
    to: z.array(z.string().trim().email().max(320)).min(1).max(20),
    subject: z.string().trim().max(200).optional(),
  })
  .strict();

const automationWebhookSchema = z
  .object({
    kind: z.literal("webhook"),
    url: z.string().trim().url().max(2000),
  })
  .strict();

export const formAutomationActionSchema = z.discriminatedUnion("kind", [
  automationSlackSchema,
  automationEmailSchema,
  automationWebhookSchema,
]);

export const formAutomationConfigSchema = z
  .object({
    on_submit: z.array(formAutomationActionSchema).default([]),
  })
  .strict();
export type FormAutomationConfig = z.infer<typeof formAutomationConfigSchema>;

// ---------------------------------------------------------------------------
// Phase 9.3 — `.partial()` defaults-leak audit. The Zod `.partial()` modifier
// makes every field optional but does NOT strip `.default(...)`. Any PATCH-
// style consumer that omits a default-bearing field will silently get the
// default written into the parsed result, then UPDATEd into the row.
// Phase 9.2 surfaced this on formUpdateSchema (status/workflow/automation);
// 9.3 audited every other `.partial()` site.
//
// Audit results (each site verified against its SPA consumer + route):
//   - partnerUpdateSchema:    FIXED — defaults on tier/status stripped via
//     .omit().extend(). No live SPA consumer today (useUpdatePartner is
//     exported but unused), but the API route is publicly exposed.
//   - dealUpdateSchema:       FIXED — default on status stripped. No live
//     consumer; defensive parity with partnerUpdateSchema.
//   - formUpdateSchema:       FIXED in Phase 9.2 (see below) — slug omitted
//     for immutability, status/workflow/automation defaults stripped.
//   - journeyUpdateSchema:    FIXED — default on execution_order stripped.
//     Real leak: journey-builder name/description autosaves and the
//     journeys-list restore PATCH would silently flip sequential journeys
//     back to "flexible" on every save.
//   - journeyTaskUpdateSchema: FIXED — defaults on assignee_kind, visibility,
//     due_offset_days, action stripped. Real leak: task-row title autosave
//     and task-config-sheet per-field autosaves would clobber configured
//     task config on every save.
//   - tierProgramUpdateSchema: FIXED — default on is_default stripped.
//     Real leak: tier-programs-list restore and tier-program-settings-form
//     name/slug/description autosaves would un-default the current default
//     program on every save.
//   - tierUpdateSchema:       no leak — tierCreateSchema has no defaults
//     (legacy_tier/color_hex/badge_path are all union/optional/transform).
//   - tierProgramMatrixRowUpdateSchema: no leak — matrixRowCreateSchema has
//     no defaults (row_kind is z.enum, label/description are required/
//     optional plain text).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Top-level form create / update schemas.
// ---------------------------------------------------------------------------
const slugSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{1,63}$/, {
    message:
      "Slug must start with a lowercase letter and use only lowercase letters, digits and hyphens (2-64 chars)",
  });

export const formCreateSchema = z.object({
  slug: slugSchema,
  name: requiredText(160),
  description: optionalText,
  status: z.enum(FORM_STATUSES).default("draft"),
  definition: formDefinitionSchema,
  workflow: formWorkflowConfigSchema.default({
    acceptance: { downstream_kind: "none", auto_accept: false, mapping: {} },
  }),
  automation: formAutomationConfigSchema.default({ on_submit: [] }),
});
export type FormCreateInput = z.infer<typeof formCreateSchema>;

// Slug is immutable post-create — external links and embed iframes rely on
// it. If you really need to rename a form, archive it and re-publish under a
// new slug.
//
// status / workflow / automation are re-defined as optional WITHOUT `.default()`
// because Zod's `.default()` survives `.partial()` and re-applies the default
// to undefined inputs — every PATCH to updateForm() that omitted these would
// otherwise silently reset them. Phase 9.1's slow-3G drag-reorder test
// surfaced this: reorder-fields silently un-published forms + reset workflow
// / automation to defaults. Lifecycle endpoints (publishForm / archiveForm /
// unpublishForm in apps/api/src/services/forms.ts) pass `status` explicitly
// so they still drive the right values; the create path uses
// formCreateSchema which keeps defaults intact.
export const formUpdateSchema = formCreateSchema
  .partial()
  .omit({ slug: true, status: true, workflow: true, automation: true })
  .extend({
    status: z.enum(FORM_STATUSES).optional(),
    workflow: formWorkflowConfigSchema.optional(),
    automation: formAutomationConfigSchema.optional(),
  });
export type FormUpdateInput = z.infer<typeof formUpdateSchema>;

// ---------------------------------------------------------------------------
// Submission create input — used by both the partner-portal server action
// and the public embed API route.
// ---------------------------------------------------------------------------
export const formSubmissionCreateSchema = z.object({
  form_id: z.string().uuid(),
  // The payload is validated *dynamically* against the form's definition
  // (see lib/forms.ts → buildSubmissionPayloadValidator); we keep it loose
  // at the top-level Zod layer so the static type doesn't have to vary by
  // form id.
  payload: z.record(z.string(), z.unknown()),
  submitter_email: z
    .union([z.literal(""), emailField])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  submitter_name: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type FormSubmissionCreateInput = z.infer<typeof formSubmissionCreateSchema>;

// ============================================================================
// Journeys — Session 4 (Track 4A).
//
// A Journey is a reusable, multi-step template. `journey_tasks` are ordered
// by `position`. Assignments fan out into per-partner `journey_task_progress`
// rows via a DB trigger; each progress row snapshots its visibility from the
// originating task so RLS gating stays cheap.
//
// Mirrors the enums + columns in supabase/migrations/0034_journeys.sql.
// ============================================================================

export const JOURNEY_EXECUTION_ORDERS = ["flexible", "sequential"] as const;
export type JourneyExecutionOrder = (typeof JOURNEY_EXECUTION_ORDERS)[number];

export const JOURNEY_TASK_ASSIGNEE_KINDS = ["partner", "vendor"] as const;
export type JourneyTaskAssigneeKind = (typeof JOURNEY_TASK_ASSIGNEE_KINDS)[number];

export const JOURNEY_TASK_VISIBILITIES = ["public", "internal"] as const;
export type JourneyTaskVisibility = (typeof JOURNEY_TASK_VISIBILITIES)[number];

export const JOURNEY_TASK_ACTION_KINDS = [
  "none",
  "form_submission",
  // Sub-bucket 4 (migration 0069). Proof-requiring kinds — the partner
  // supplies a discriminated proof_payload when completing the task.
  "note",
  "link",
  "attachment",
  // Sub-bucket 14 (Phase 7). Admin-content kinds — admin authors the
  // payload inline on the task; partner just acknowledges (no proof).
  // Completion routes through the same status-flip path as `none`.
  "info_note",
  "info_link",
  "info_attachment",
] as const;
export type JourneyTaskActionKind = (typeof JOURNEY_TASK_ACTION_KINDS)[number];

// Display labels — single source of truth for the admin + portal UIs.
export const JOURNEY_EXECUTION_ORDER_LABEL: Record<JourneyExecutionOrder, string> = {
  flexible: "Flexible",
  sequential: "Sequential",
};

export const JOURNEY_TASK_ASSIGNEE_KIND_LABEL: Record<JourneyTaskAssigneeKind, string> = {
  partner: "Partner",
  vendor: "Vendor",
};

export const JOURNEY_TASK_VISIBILITY_LABEL: Record<JourneyTaskVisibility, string> = {
  public: "Public (partner-visible)",
  internal: "Internal only",
};

export const JOURNEY_TASK_ACTION_KIND_LABEL: Record<JourneyTaskActionKind, string> = {
  none: "No action",
  form_submission: "Form submission",
  note: "Note required",
  link: "Link required",
  attachment: "Attachment required",
  info_note: "Shared note",
  info_link: "Shared link",
  info_attachment: "Shared file",
};

// ---------------------------------------------------------------------------
// journey_tasks.action — discriminated union over the action kinds. Stored as
// JSONB on journey_tasks.action (migration 0034); the database has no enum
// constraint, so adding new variants is additive and safe for existing rows.
//
//   `none` = manual completion only (toggle in the portal).
//   `form_submission` = "Submit form" button in the portal; completion is
//      gated by `complete_on`:
//        - 'submitted' = task completes when the partner submits the form
//        - 'accepted'  = task completes when an internal user accepts the
//                        resulting submission via /admin/submissions.
//   `note` / `link` / `attachment` (sub-bucket 4) = partner-proof kinds —
//      the partner records a discriminated proof payload alongside the status
//      flip. `prompt` surfaces optional admin-authored hint copy (e.g. "Paste
//      the campaign brief link"). `attachment.multiple` decides whether the
//      portal renders single- or multi-file upload UI.
//   `info_note` / `info_link` / `info_attachment` (sub-bucket 14) =
//      admin-content kinds — admin authors the body/href/asset on the task,
//      partner just acknowledges (status flip with no proof payload). Used
//      for "read this" / "follow this link" / "download this file" steps.
// ---------------------------------------------------------------------------
export const journeyTaskActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }).strict(),
  z
    .object({
      kind: z.literal("form_submission"),
      form_id: z.string().uuid(),
      complete_on: z.enum(["submitted", "accepted"]).default("accepted"),
    })
    .strict(),
  z
    .object({
      kind: z.literal("note"),
      prompt: z.string().trim().max(500).nullable().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("link"),
      prompt: z.string().trim().max(500).nullable().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("attachment"),
      prompt: z.string().trim().max(500).nullable().optional(),
      multiple: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      kind: z.literal("info_note"),
      body: z.string().trim().min(1).max(4000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("info_link"),
      href: z.string().trim().url().max(2048),
      label: z.string().trim().max(200).nullable().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("info_attachment"),
      asset_id: z.string().uuid(),
    })
    .strict(),
]);
export type JourneyTaskAction = z.infer<typeof journeyTaskActionSchema>;

// ---------------------------------------------------------------------------
// journey_task_progress.proof_payload — discriminated shape mirroring the
// action.kind on the linked journey_tasks row. Stored as JSONB; the column
// is null for `none` and `form_submission` action kinds.
// ---------------------------------------------------------------------------
export const journeyTaskProofPayloadSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("note"), note: z.string().trim().min(1).max(4000) }).strict(),
  z.object({ kind: z.literal("link"), url: z.string().trim().url().max(2048) }).strict(),
  z
    .object({
      kind: z.literal("attachment"),
      attachment_ids: z.array(z.string().uuid()).min(1).max(10),
    })
    .strict(),
]);
export type JourneyTaskProofPayload = z.infer<typeof journeyTaskProofPayloadSchema>;

// ---------------------------------------------------------------------------
// Journey + journey_task create/update inputs.
// ---------------------------------------------------------------------------
export const journeyCreateSchema = z.object({
  name: requiredText(200),
  description: optionalText,
  execution_order: z.enum(JOURNEY_EXECUTION_ORDERS).default("flexible"),
});
export type JourneyCreateInput = z.infer<typeof journeyCreateSchema>;

// Phase 9.3 — `execution_order` has `.default("flexible")` on create. The
// SPA journey-builder + list page both PATCH partial bodies:
//   - autosave: `{ name }`, `{ description }`
//   - restore from archive: `{ is_archived: false }`
// Through `.partial()` each of these would silently UPDATE execution_order
// back to "flexible" — clobbering any sequential journey on every name
// autosave. Rebuild via `.omit().extend()` to strip the default.
export const journeyUpdateSchema = journeyCreateSchema
  .partial()
  .omit({ execution_order: true })
  .extend({
    execution_order: z.enum(JOURNEY_EXECUTION_ORDERS).optional(),
    is_archived: z.boolean().optional(),
  });
export type JourneyUpdateInput = z.infer<typeof journeyUpdateSchema>;

const dueOffsetDays = z.coerce.number().int().min(0).max(400);

export const journeyTaskCreateSchema = z.object({
  title: requiredText(200),
  description: optionalText,
  assignee_kind: z.enum(JOURNEY_TASK_ASSIGNEE_KINDS).default("partner"),
  default_assignee_user_id: z
    .union([z.literal(""), z.string().uuid(), z.null()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  visibility: z.enum(JOURNEY_TASK_VISIBILITIES).default("public"),
  due_offset_days: dueOffsetDays.default(0),
  action: journeyTaskActionSchema.default({ kind: "none" }),
});
export type JourneyTaskCreateInput = z.infer<typeof journeyTaskCreateSchema>;

// Phase 9.3 — `assignee_kind`, `visibility`, `due_offset_days`, `action` all
// carry `.default(...)` on create. The SPA task-row title autosave + the
// task-config-sheet per-field autosave both PATCH partial bodies (e.g.
// `{ title }` from the title row). Through `.partial()` every PATCH would
// silently UPDATE the task back to assignee="partner", visibility="public",
// due_offset_days=0, action={ kind: "none" } — clobbering whatever the user
// had configured. Rebuild via `.omit().extend()` to strip the defaults.
export const journeyTaskUpdateSchema = journeyTaskCreateSchema
  .partial()
  .omit({
    assignee_kind: true,
    visibility: true,
    due_offset_days: true,
    action: true,
  })
  .extend({
    assignee_kind: z.enum(JOURNEY_TASK_ASSIGNEE_KINDS).optional(),
    visibility: z.enum(JOURNEY_TASK_VISIBILITIES).optional(),
    due_offset_days: dueOffsetDays.optional(),
    action: journeyTaskActionSchema.optional(),
  });
export type JourneyTaskUpdateInput = z.infer<typeof journeyTaskUpdateSchema>;

// ---------------------------------------------------------------------------
// Bulk-assign input.
// ---------------------------------------------------------------------------
export const assignJourneyInputSchema = z.object({
  journey_id: z.string().uuid(),
  partner_ids: z.array(z.string().uuid()).min(1).max(500),
});
export type AssignJourneyInput = z.infer<typeof assignJourneyInputSchema>;

// ---------------------------------------------------------------------------
// Status-history entry shape (matches tg_journey_task_progress_status_history()).
// ---------------------------------------------------------------------------
export type JourneyTaskProgressStatusHistoryEntry =
  | { status: TaskStatus; changed_at: string; changed_by: string }
  | { from: TaskStatus; to: TaskStatus; changed_at: string; changed_by: string };

// ---------------------------------------------------------------------------
// Strongly-typed row shapes for the application layer. JSONB columns are
// narrowed via the Zod schemas above; the loose `Json` versions live in
// types/database.types.ts.
// ---------------------------------------------------------------------------
export type JourneyRow = {
  id: string;
  name: string;
  description: string | null;
  execution_order: JourneyExecutionOrder;
  is_system: boolean;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type JourneyTaskRow = {
  id: string;
  journey_id: string;
  position: number;
  title: string;
  description: string | null;
  assignee_kind: JourneyTaskAssigneeKind;
  default_assignee_user_id: string | null;
  visibility: JourneyTaskVisibility;
  due_offset_days: number;
  action: JourneyTaskAction;
  created_at: string;
  updated_at: string;
};

export type JourneyAssignmentRow = {
  id: string;
  journey_id: string;
  partner_id: string;
  assigned_at: string;
  assigned_by: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type JourneyTaskProgressRow = {
  id: string;
  assignment_id: string;
  journey_task_id: string;
  partner_id: string;
  journey_id: string;
  visibility: JourneyTaskVisibility;
  due_at: string | null;
  status: TaskStatus;
  status_history: JourneyTaskProgressStatusHistoryEntry[];
  completing_submission_id: string | null;
  completed_at: string | null;
  completed_by: string | null;
  // Sub-bucket 4 (migration 0069). null for non-snoozed rows.
  snoozed_until: string | null;
  snooze_reason: string | null;
  // Sub-bucket 4 (migration 0069). Discriminated proof payload — null when
  // the linked task.action.kind is `none` or `form_submission`. Shape
  // validated by journeyTaskProofPayloadSchema.
  proof_payload: JourneyTaskProofPayload | null;
  created_at: string;
  updated_at: string;
};

// Sub-bucket 4 — per-progress comment + attachment row shapes. Mirrors the
// MDF equivalents from bucket 9; the domain layer keeps comment/attachment
// shapes uniform across surfaces so list/timeline renderers can share helpers.
export type JourneyTaskCommentRow = {
  id: string;
  progress_id: string;
  author_user_id: string | null;
  author_role: "partner" | "internal";
  body: string;
  created_at: string;
};

export type JourneyTaskAttachmentRow = {
  id: string;
  progress_id: string;
  partner_id: string;
  storage_key: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  created_at: string;
};

// `loadJourneyDetail()` aggregate — journey + ordered tasks + assignment count.
export type JourneyDetail = {
  journey: JourneyRow;
  tasks: JourneyTaskRow[];
  assignment_count: number;
};

// ============================================================================
// Tier Programs — Session 5 (Track 5A).
//
// Replaces the hardcoded `partner_tier` enum (which stays as a deprecated
// mirror, maintained by tg_sync_partner_legacy_tier in 0038 so existing RLS /
// storage / auth_partner_tier() keep working). The new model is a fully
// editable matrix per program: tiers as columns, requirements/benefits as
// drag-orderable rows, free-text cells.
//
// Mirrors supabase/migrations/0038_tier_programs.sql.
// No seed data — Bookboost configures their program via /admin/tiers post-cutover.
// ============================================================================

export const TIER_PROGRAM_ROW_KINDS = ["requirement", "benefit"] as const;
export type TierProgramRowKind = (typeof TIER_PROGRAM_ROW_KINDS)[number];

export const TIER_PROGRAM_ROW_KIND_LABEL: Record<TierProgramRowKind, string> = {
  requirement: "Requirement",
  benefit: "Benefit",
};

// The tier_programs table row shape.
export type TierProgramRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
};

export type Tier = {
  id: string;
  program_id: string;
  name: string;
  slug: string;
  legacy_tier: PartnerTier | null;
  color_hex: string | null;
  badge_path: string | null;
  position: number;
};

// A row of the matrix (requirement OR benefit), spanning all tiers in the
// program. `TierProgramMatrixRow` rather than `TierProgramRow` to avoid the
// "is it the program or a matrix row?" name collision.
export type TierProgramMatrixRow = {
  id: string;
  program_id: string;
  row_kind: TierProgramRowKind;
  label: string;
  description: string | null;
  position: number;
};

export type TierProgramCell = {
  id: string;
  row_id: string;
  tier_id: string;
  value: string | null;
};

export type PartnerTierAssignmentRow = {
  id: string;
  partner_id: string;
  program_id: string;
  tier_id: string;
  assigned_at: string;
  assigned_by: string | null;
};

// `loadTierProgramDetail()` aggregate — program + tiers + matrix rows + cells.
export type TierProgramDetail = {
  program: TierProgramRow;
  tiers: Tier[];
  rows: TierProgramMatrixRow[];
  cells: TierProgramCell[];
};

// ---------------------------------------------------------------------------
// Zod schemas — slug regex matches the form-slug pattern (lowercase, digits,
// hyphens; 2-64 chars). Same shape as the forms slug for consistency.
// ---------------------------------------------------------------------------
const tierSlugSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{1,63}$/, {
    message:
      "Slug must start with a lowercase letter and use only lowercase letters, digits and hyphens (2-64 chars)",
  });

export const tierProgramCreateSchema = z.object({
  name: requiredText(80),
  slug: tierSlugSchema,
  description: optionalText,
  is_default: z.coerce.boolean().default(false),
});
export type TierProgramCreateInput = z.infer<typeof tierProgramCreateSchema>;

// Phase 9.3 — `is_default` has `.default(false)` on create. The SPA tier-
// programs list (restore button) + tier-program-settings-form (per-field
// name/slug/description autosave) both PATCH partial bodies. Through
// `.partial()` each PATCH would silently UPDATE the row to is_default=false
// — meaning the current default program would un-default itself on any
// rename or restore. Rebuild via `.omit().extend()`.
export const tierProgramUpdateSchema = tierProgramCreateSchema
  .partial()
  .omit({ is_default: true })
  .extend({
    is_default: z.coerce.boolean().optional(),
    is_archived: z.boolean().optional(),
  });
export type TierProgramUpdateInput = z.infer<typeof tierProgramUpdateSchema>;

const hexColor = z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, {
  message: "Use a 6-digit hex colour, e.g. #c0a062",
});

export const tierCreateSchema = z.object({
  name: requiredText(80),
  slug: tierSlugSchema,
  legacy_tier: z
    .union([z.literal(""), z.enum(PARTNER_TIERS)])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  color_hex: z
    .union([z.literal(""), hexColor])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  // Optional path inside the `content` bucket — set after uploadTierBadge.
  badge_path: z
    .string()
    .trim()
    .max(500)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type TierCreateInput = z.infer<typeof tierCreateSchema>;

export const tierUpdateSchema = tierCreateSchema.partial();
export type TierUpdateInput = z.infer<typeof tierUpdateSchema>;

export const tierProgramMatrixRowCreateSchema = z.object({
  row_kind: z.enum(TIER_PROGRAM_ROW_KINDS),
  label: requiredText(160),
  description: optionalText,
});
export type TierProgramMatrixRowCreateInput = z.infer<typeof tierProgramMatrixRowCreateSchema>;

export const tierProgramMatrixRowUpdateSchema = tierProgramMatrixRowCreateSchema.partial();
export type TierProgramMatrixRowUpdateInput = z.infer<typeof tierProgramMatrixRowUpdateSchema>;

export const tierProgramCellUpsertSchema = z.object({
  row_id: z.string().uuid(),
  tier_id: z.string().uuid(),
  value: z
    .string()
    .max(500)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type TierProgramCellUpsertInput = z.infer<typeof tierProgramCellUpsertSchema>;

export const partnerTierAssignSchema = z.object({
  partner_id: z.string().uuid(),
  program_id: z.string().uuid(),
  tier_id: z.string().uuid(),
});
export type PartnerTierAssignInput = z.infer<typeof partnerTierAssignSchema>;

// Bulk clear: reset a batch of partners to the empty tier state. Capped to the
// admin list size (500) to bound a single request.
export const partnerTierBulkClearSchema = z.object({
  partner_ids: z.array(z.string().uuid()).min(1).max(500),
});
export type PartnerTierBulkClearInput = z.infer<typeof partnerTierBulkClearSchema>;

// ============================================================================
// Notifications — Session 7 (Track 7A).
//
// One row per (event, audience, channel-config) in public.notification_rules.
// Track 7B adds public.notification_deliveries; this module declares only the
// payload + rule shapes needed by the dispatcher (lib/notifications.ts) and
// the admin UI at /admin/settings/notifications.
//
// Mirrors supabase/migrations/0042_notifications.sql.
// ============================================================================

export const NOTIFICATION_EVENT_KEYS = [
  "DEAL_REGISTERED",
  "DEAL_STATUS_CHANGED",
  "DEAL_CLOSED_WON",
  "COMMENT_POSTED",
  "OBJECT_SLEEPING",
  "ANNOUNCEMENT_PUBLISHED",
  "SUBMISSION_RECEIVED",
  "SUBMISSION_ACCEPTED",
  "SUBMISSION_DECLINED",
  "PARTNER_ONBOARDED",
  "PARTNER_INVITED",
  "ONBOARDING_COMPLETED",
  "TASK_NUDGE",
  // Session 8 — MDF Phase A. Seeded in 0048_mdf_seeds.sql.
  "MDF_REQUEST_SUBMITTED",
  "MDF_REQUEST_APPROVED",
  "MDF_REQUEST_DECLINED",
] as const;
export type NotificationEventKey = (typeof NOTIFICATION_EVENT_KEYS)[number];

export const NOTIFICATION_EVENT_KEY_LABEL: Record<NotificationEventKey, string> = {
  DEAL_REGISTERED: "New deal registered",
  DEAL_STATUS_CHANGED: "Deal status changed",
  DEAL_CLOSED_WON: "Deal closed won",
  COMMENT_POSTED: "Deal comment posted",
  OBJECT_SLEEPING: "Stale object detected",
  ANNOUNCEMENT_PUBLISHED: "Announcement published",
  SUBMISSION_RECEIVED: "Form submission received",
  SUBMISSION_ACCEPTED: "Form submission accepted",
  SUBMISSION_DECLINED: "Form submission declined",
  PARTNER_ONBOARDED: "New partner joined",
  PARTNER_INVITED: "Partner contact invited",
  ONBOARDING_COMPLETED: "Partner onboarding complete",
  TASK_NUDGE: "Journey task nudge",
  MDF_REQUEST_SUBMITTED: "MDF request submitted",
  MDF_REQUEST_APPROVED: "MDF request approved",
  MDF_REQUEST_DECLINED: "MDF request declined",
};

export const NOTIFICATION_CHANNELS = ["mail", "chat"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// ---------------------------------------------------------------------------
// Payload union — discriminated by event_key. Every variant carries
// `occurred_at` (ISO timestamp used for default idempotency keys) and the
// event-specific fields needed by both the React Email components and the
// Slack templates (lib/email.ts + lib/slack.ts).
// ---------------------------------------------------------------------------
export type NotificationEventPayload =
  | {
      event_key: "DEAL_REGISTERED";
      deal_id: string;
      partner_id: string;
      prospect_company: string;
      deal_type: DealType;
      estimated_value_eur: number | null;
      partner_name: string;
      occurred_at: string;
    }
  | {
      event_key: "DEAL_STATUS_CHANGED";
      deal_id: string;
      partner_id: string;
      prospect_company: string;
      partner_name: string;
      from_status: DealStatus;
      to_status: DealStatus;
      // ISO timestamp of when the status flip occurred. Forms part of the
      // default idempotency key so admin + HubSpot-webhook callers don't
      // double-fire the same logical change.
      changed_at: string;
      source: "admin" | "hubspot_webhook";
      occurred_at: string;
    }
  | {
      event_key: "DEAL_CLOSED_WON";
      deal_id: string;
      partner_id: string;
      prospect_company: string;
      partner_name: string;
      estimated_value_eur: number | null;
      occurred_at: string;
    }
  | {
      event_key: "COMMENT_POSTED";
      comment_id: string;
      deal_id: string;
      partner_id: string;
      prospect_company: string;
      author_user_id: string;
      author_name: string;
      author_type: CommentAuthorType;
      // Truncated to ~400 chars by the caller (matches existing pattern).
      body_excerpt: string;
      occurred_at: string;
    }
  | {
      event_key: "OBJECT_SLEEPING";
      object_kind: "deal" | "submission";
      object_id: string;
      partner_id: string | null;
      days_idle: number;
      occurred_at: string;
    }
  | {
      event_key: "ANNOUNCEMENT_PUBLISHED";
      announcement_id: string;
      body_excerpt: string;
      occurred_at: string;
    }
  | {
      event_key:
        | "SUBMISSION_RECEIVED"
        | "SUBMISSION_ACCEPTED"
        | "SUBMISSION_DECLINED";
      submission_id: string;
      form_id: string;
      form_name: string;
      partner_id: string | null;
      submitter_user_id: string | null;
      submitter_email: string | null;
      submitter_name: string | null;
      downstream_kind?: SubmissionDownstreamKind;
      downstream_id?: string;
      // Decline-only; ignored on RECEIVED + ACCEPTED.
      note?: string | null;
      occurred_at: string;
    }
  | {
      event_key: "PARTNER_ONBOARDED";
      partner_id: string;
      partner_name: string;
      partner_type: PartnerType;
      occurred_at: string;
    }
  | {
      event_key: "PARTNER_INVITED";
      partner_id: string;
      partner_name: string;
      invitee_email: string;
      invitee_name: string | null;
      invite_token: string;
      invite_url: string;
      occurred_at: string;
    }
  | {
      event_key: "ONBOARDING_COMPLETED";
      partner_id: string;
      partner_name: string;
      occurred_at: string;
    }
  | {
      event_key: "TASK_NUDGE";
      task_id: string;
      task_title: string;
      journey_id: string;
      journey_name: string;
      partner_id: string;
      reason: "created" | "due_soon" | "overdue";
      due_at: string | null;
      occurred_at: string;
    }
  // ---------------------------------------------------------------------
  // Session 8 — MDF Phase A. SUBMITTED targets workspace admins (natural
  // recipient resolves to admins helper). APPROVED + DECLINED target the
  // partner's primary contact. DECLINED additionally carries the reason.
  // ---------------------------------------------------------------------
  | {
      event_key: "MDF_REQUEST_SUBMITTED";
      mdf_request_id: string;
      fund_id: string;
      partner_id: string;
      amount_eur: number;
      occurred_at: string;
    }
  | {
      event_key: "MDF_REQUEST_APPROVED";
      mdf_request_id: string;
      fund_id: string;
      partner_id: string;
      amount_eur: number;
      occurred_at: string;
    }
  | {
      event_key: "MDF_REQUEST_DECLINED";
      mdf_request_id: string;
      fund_id: string;
      partner_id: string;
      amount_eur: number;
      declined_reason: string;
      occurred_at: string;
    };

export type NotificationEventPayloadByKey<K extends NotificationEventKey> =
  Extract<NotificationEventPayload, { event_key: K }>;

// ---------------------------------------------------------------------------
// Row shape exposed in lib/notifications.ts → loadActiveRulesFor() and the
// admin server actions. Narrows what's loose (`Json`) in
// types/database.types.ts.
// ---------------------------------------------------------------------------
export type NotificationRuleRow = {
  id: string;
  event_key: NotificationEventKey;
  name: string;
  description: string | null;
  audience_segment_id: string | null;
  include_natural_recipient: boolean;
  mail_enabled: boolean;
  mail_subject_override: string | null;
  chat_enabled: boolean;
  chat_prefix_override: string | null;
  is_system: boolean;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Zod schemas — used by the admin server actions in
// app/admin/settings/notifications/_actions.ts.
// ---------------------------------------------------------------------------
// Every field is wrapped in `.optional()` (or `.optional().transform()`) so
// the inferred type has true `| undefined` on each key. The admin UI sends
// just the keys it's editing per call; everything else stays at server
// defaults.
export const notificationRuleUpdateSchema = z.object({
  name: requiredText(160).optional(),
  description: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v))
    .optional(),
  audience_segment_id: z
    .union([z.literal(""), z.string().uuid()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v))
    .optional(),
  include_natural_recipient: z.boolean().optional(),
  mail_enabled: z.boolean().optional(),
  mail_subject_override: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v))
    .optional(),
  chat_enabled: z.boolean().optional(),
  chat_prefix_override: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v))
    .optional(),
  active: z.boolean().optional(),
});
export type NotificationRuleUpdateInput = z.infer<
  typeof notificationRuleUpdateSchema
>;

export const notificationRuleCreateSchema = z.object({
  event_key: z.enum(NOTIFICATION_EVENT_KEYS),
  name: requiredText(160),
  description: optionalText,
  audience_segment_id: z
    .union([z.literal(""), z.string().uuid()])
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  include_natural_recipient: z.coerce.boolean().default(true),
  mail_enabled: z.coerce.boolean().default(false),
  mail_subject_override: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  chat_enabled: z.coerce.boolean().default(false),
  chat_prefix_override: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
  active: z.coerce.boolean().default(true),
});
export type NotificationRuleCreateInput = z.infer<
  typeof notificationRuleCreateSchema
>;

// ============================================================================
// Marketing Development Funds (MDF) — Session 8 Track 8A.
//
// Mirrors supabase/migrations/0046_mdf.sql. Three tables: marketing_funds,
// mdf_allocations, mdf_requests. Phase A: vendor creates funds + manual
// per-partner allocations; partner submits requests; 4-state machine
// (pending → approved → spent | declined). All amounts integer cents-units
// in EUR (CHECK constraint enforces single-currency Phase A).
// ============================================================================

export const MDF_FUND_STATUSES = ["open", "paused", "closed"] as const;
export type MdfFundStatus = (typeof MDF_FUND_STATUSES)[number];

export const MDF_FUND_STATUS_LABEL: Record<MdfFundStatus, string> = {
  open: "Open",
  paused: "Paused",
  closed: "Closed",
};

export const MDF_REQUEST_STATUSES = [
  "pending",
  "approved",
  "spent",
  "declined",
] as const;
export type MdfRequestStatus = (typeof MDF_REQUEST_STATUSES)[number];

export const MDF_REQUEST_STATUS_LABEL: Record<MdfRequestStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  spent: "Spent",
  declined: "Declined",
};

// ---------------------------------------------------------------------------
// Zod schemas. Integer-EUR amounts; slug is the unique URL-safe handle.
// Slugs follow the existing pattern (lower-case kebab, 1-80 chars).
// ---------------------------------------------------------------------------

const mdfSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
    message: "Slug must be lower-case letters, digits or '-'.",
  });

const integerEuroAmount = (min: number) =>
  z
    .number()
    .int("Must be a whole number of EUR")
    .min(min, `Must be ≥ ${min}`);

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD format");

export const marketingFundCreateSchema = z
  .object({
    name: requiredText(200),
    slug: mdfSlugSchema,
    total_amount_eur: integerEuroAmount(0),
    period_start: isoDate,
    period_end: isoDate,
    notes: optionalText,
    status: z.enum(MDF_FUND_STATUSES).default("open"),
  })
  .refine(
    (v) => new Date(v.period_end) >= new Date(v.period_start),
    { message: "period_end must be on or after period_start", path: ["period_end"] }
  );
// Use the schema's *input* type so optional fields with .transform stay
// optional at the call site (z.infer returns the *output* type, where
// `optionalText` resolves to `string | null` — too strict for caller code).
export type MarketingFundCreateInput = z.input<typeof marketingFundCreateSchema>;

export const marketingFundUpdateSchema = z
  .object({
    name: requiredText(200).optional(),
    total_amount_eur: integerEuroAmount(0).optional(),
    period_start: isoDate.optional(),
    period_end: isoDate.optional(),
    notes: optionalText,
    status: z.enum(MDF_FUND_STATUSES).optional(),
  })
  .refine(
    (v) =>
      v.period_start == null ||
      v.period_end == null ||
      new Date(v.period_end) >= new Date(v.period_start),
    { message: "period_end must be on or after period_start", path: ["period_end"] }
  );
export type MarketingFundUpdateInput = z.input<typeof marketingFundUpdateSchema>;

export const mdfAllocationCreateSchema = z.object({
  fund_id: z.string().uuid(),
  partner_id: z.string().uuid(),
  cap_amount_eur: integerEuroAmount(0),
  notes: optionalText,
});
export type MdfAllocationCreateInput = z.input<typeof mdfAllocationCreateSchema>;

export const mdfAllocationUpdateSchema = z.object({
  cap_amount_eur: integerEuroAmount(0).optional(),
  notes: optionalText,
});
export type MdfAllocationUpdateInput = z.input<typeof mdfAllocationUpdateSchema>;

// Partner-side request creation. amount_eur > 0; campaign_name required;
// supporting_doc_url is a Supabase storage path (e.g. "1736923-mdf-requests-..."),
// not a full URL. The portal action uploads via lib/storage.ts → uploadContent
// and stores the returned path. Both portal + admin drawers call signedDownloadUrl
// at render time. External URLs are accepted (renderers branch on /^https?:\/\//).
export const mdfRequestCreateSchema = z.object({
  allocation_id: z.string().uuid(),
  amount_eur: integerEuroAmount(1),
  campaign_name: requiredText(200),
  campaign_type: optionalText,
  description: optionalText,
  supporting_doc_url: z
    .string()
    .trim()
    .max(2048)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type MdfRequestCreateInput = z.input<typeof mdfRequestCreateSchema>;

export const mdfRequestApproveSchema = z.object({
  id: z.string().uuid(),
});
export type MdfRequestApproveInput = z.infer<typeof mdfRequestApproveSchema>;

export const mdfRequestDeclineSchema = z.object({
  id: z.string().uuid(),
  declined_reason: requiredText(1000),
});
export type MdfRequestDeclineInput = z.infer<typeof mdfRequestDeclineSchema>;

export const mdfRequestSpendSchema = z.object({
  id: z.string().uuid(),
});
export type MdfRequestSpendInput = z.infer<typeof mdfRequestSpendSchema>;

// ---------------------------------------------------------------------------
// Read row shapes — what lib/mdf.ts returns to UI consumers. Narrows the
// loose `Database` row types into the trimmed shape the pages actually use.
// ---------------------------------------------------------------------------
export type MarketingFundRow = {
  id: string;
  name: string;
  slug: string;
  total_amount_eur: number;
  period_start: string;
  period_end: string;
  status: MdfFundStatus;
  currency: "EUR";
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type MdfAllocationRow = {
  id: string;
  fund_id: string;
  partner_id: string;
  cap_amount_eur: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type MdfRequestRow = {
  id: string;
  fund_id: string;
  allocation_id: string;
  partner_id: string;
  requested_by: string;
  amount_eur: number;
  campaign_name: string;
  campaign_type: string | null;
  description: string | null;
  supporting_doc_url: string | null;
  status: MdfRequestStatus;
  status_history: unknown;
  approved_by: string | null;
  approved_at: string | null;
  declined_reason: string | null;
  spent_at: string | null;
  created_at: string;
  updated_at: string;
};

// Session 10 Track 10C's per-partner Analytics dashboards (PartnerDashboardRow,
// PARTNER_DASHBOARD_EMBED_KINDS, etc.) were removed in migration
// 0060_drop_partner_dashboards.sql (2026-05-18) when the iframe-embed model
// was replaced by native-chart analytics. The new typings will land alongside
// the replacement components.

// ============================================================================
// Portal content (Session 6 — Path A portal experience parity)
//
// `portal_content` is the small content-config layer that replaces Introw's
// no-code Experience builder. Rows are keyed on (scope_kind, scope_value, key,
// position); the resolver in lib/portal-content.ts picks the most-specific row
// matching the viewing partner (partner_type > tier > all).
//
// `portal_faq_items` is a separate table for structured FAQ entries with
// per-tier visibility (mirrors content_assets's visibility evaluation).
// ============================================================================

export const PORTAL_CONTENT_SCOPE_KINDS = ["all", "tier", "partner_type"] as const;
export type PortalContentScopeKind = (typeof PORTAL_CONTENT_SCOPE_KINDS)[number];

export const PORTAL_CONTENT_KEYS = ["welcome", "level_up", "support_links"] as const;
export type PortalContentKey = (typeof PORTAL_CONTENT_KEYS)[number];

// Per-key content shapes. Stored as JSONB; validated with Zod on the write
// path so admins can't author broken rows that crash the renderer.
export const portalWelcomeContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(2000),
  cover_image_url: z.string().url().nullable().optional(),
  primary_cta: z
    .object({ label: z.string().trim().min(1).max(60), href: z.string().min(1).max(500) })
    .nullable()
    .optional(),
  secondary_cta: z
    .object({ label: z.string().trim().min(1).max(60), href: z.string().min(1).max(500) })
    .nullable()
    .optional(),
});
export type PortalWelcomeContent = z.infer<typeof portalWelcomeContentSchema>;

export const portalLevelUpContentSchema = z.object({
  heading: z.string().trim().min(1).max(200),
  subheading: z.string().trim().max(500).optional(),
  // The tier matrix is read from tier_programs (Session 5). This schema only
  // carries the textual copy that wraps the live matrix.
  cta_label: z.string().trim().max(60).optional(),
  cta_href: z.string().max(500).optional(),
});
export type PortalLevelUpContent = z.infer<typeof portalLevelUpContentSchema>;

export const portalSupportLinksContentSchema = z.object({
  heading: z.string().trim().min(1).max(200),
  links: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(120),
        href: z.string().min(1).max(500),
        description: z.string().trim().max(300).optional(),
      }),
    )
    .min(0)
    .max(20),
});
export type PortalSupportLinksContent = z.infer<typeof portalSupportLinksContentSchema>;

// Discriminated by key. The lib resolver returns the typed content for each
// key; callers narrow by key before reading.
export type PortalContentForKey = {
  welcome: PortalWelcomeContent;
  level_up: PortalLevelUpContent;
  support_links: PortalSupportLinksContent;
};

export type PortalContentRow = {
  id: string;
  scope_kind: PortalContentScopeKind;
  scope_value: string | null;
  key: PortalContentKey;
  content: PortalContentForKey[PortalContentKey];
  position: number;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

// FAQ — uses asset_visibility (all_partners / tier_silver_up / tier_gold_up /
// internal_only). `internal_only` rows are author-only previews.
export type PortalFaqItem = {
  id: string;
  category: string;
  question: string;
  answer_md: string;
  position: number;
  visibility: AssetVisibility;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

// `loadPortalContent({ partner, key })` aggregate — the resolved row plus
// the candidates that fell through, useful for the admin "preview-as-partner"
// affordance.
export type PortalContentResolution<K extends PortalContentKey> = {
  key: K;
  resolved: (PortalContentRow & { content: PortalContentForKey[K] }) | null;
  candidates: PortalContentRow[];
};

// Zod schemas for the write path. The content field is validated against the
// per-key schema in lib/portal-content.ts; this outer schema enforces the
// scope/key/position envelope.
export const portalContentScopeSchema = z.discriminatedUnion("scope_kind", [
  z.object({ scope_kind: z.literal("all"), scope_value: z.null() }),
  z.object({ scope_kind: z.literal("tier"), scope_value: z.enum(PARTNER_TIERS) }),
  z.object({
    scope_kind: z.literal("partner_type"),
    scope_value: z.enum(PARTNER_TYPES),
  }),
]);

export const portalContentWriteSchema = z
  .object({
    key: z.enum(PORTAL_CONTENT_KEYS),
    position: z.number().int().min(0).max(1000),
    active: z.boolean(),
    content: z.unknown(),
  })
  .and(portalContentScopeSchema);
export type PortalContentWriteInput = z.infer<typeof portalContentWriteSchema>;

export const portalFaqItemWriteSchema = z.object({
  category: z.string().trim().min(1).max(100),
  question: z.string().trim().min(1).max(500),
  answer_md: z.string().trim().min(1).max(10000),
  position: z.number().int().min(0).max(1000),
  visibility: z.enum(ASSET_VISIBILITIES),
  active: z.boolean(),
});
export type PortalFaqItemWriteInput = z.infer<typeof portalFaqItemWriteSchema>;

// Merge-field context — what the renderer in lib/merge-fields.ts can substitute.
// Extend as new tokens are added; keep the token list small (Introw also keeps
// it small — they show 8-10 tokens in the editor).
export type MergeFieldContext = {
  partner: {
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    tier: PartnerTier | null;
    partner_type: PartnerType | null;
  };
};
